import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, AlertTriangle, Ban, CheckCircle2 } from 'lucide-react';
import { formatWeight } from '@vj/shared';
import { api, ApiClientError } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { TextAreaField } from '../../components/ui/Field.jsx';
import { ThermalPrintButton } from '../../components/ThermalPrintButton.jsx';
import { PdfActions } from '../../components/PdfActions.jsx';

const ITEM_STATUS_LABEL = { ok: 'OK', short: 'Short', damaged: 'Damaged', excess: 'Unexpected / excess' };
const ITEM_STATUS_TONE = { ok: 'text-success', short: 'text-danger', damaged: 'text-danger', excess: 'text-warning' };

export default function ReceiptViewPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [cancelError, setCancelError] = useState(null);

  const { data: receipt, isLoading } = useQuery({
    queryKey: ['receipts', id],
    queryFn: () => api.get(`/receipt/${id}`),
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.post(`/receipt/${id}/cancel`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['receipts'] });
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      setCancelOpen(false);
    },
    onError: (err) => setCancelError(err instanceof ApiClientError ? err.message : 'Could not cancel this receipt.'),
  });

  if (isLoading || !receipt) return null;

  const canCancel = receipt.status !== 'cancelled' && receipt.issue_status !== 'cancelled';

  return (
    <div className="flex flex-col gap-5 pb-24">
      <div className="flex items-center gap-3">
        <Link to={`/issue-receipt/${receipt.issue_id}`} className="flex h-touch w-touch items-center justify-center rounded-card hover:bg-surface-2">
          <ArrowLeft size={20} aria-hidden="true" />
        </Link>
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">{receipt.voucher_no}</h1>
          <p className="text-sm text-muted">
            {new Date(receipt.receipt_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
            {' · against '}
            <Link to={`/issue-receipt/${receipt.issue_id}`} className="text-primary hover:underline">{receipt.issue_voucher_no}</Link>
          </p>
        </div>
        {receipt.status === 'cancelled' ? (
          <span className="ml-auto rounded-full border border-danger px-3 py-1 text-xs font-medium uppercase tracking-wide text-danger">
            Cancelled
          </span>
        ) : null}
      </div>

      {receipt.status === 'cancelled' ? (
        <p className="rounded-card border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          Cancelled {new Date(receipt.cancelled_at).toLocaleDateString('en-IN')}: {receipt.cancel_reason}
        </p>
      ) : null}

      {receipt.has_mismatch ? (
        <p role="alert" className="flex items-center gap-2 rounded-card border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          <AlertTriangle size={16} aria-hidden="true" />
          Weight mismatch: {formatWeight(Math.abs(receipt.weight_difference))} {Number(receipt.weight_difference) > 0 ? 'short' : 'excess'} against what was pending.
        </p>
      ) : null}

      <section className="card p-5">
        <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Received from</h2>
        <p className="font-medium">{receipt.received_from}</p>
        {receipt.received_from_address ? <p className="text-sm text-muted">{receipt.received_from_address}</p> : null}
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">Items</h2>
        <div className="table-scroll">
          <table className="w-full min-w-[620px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
                <th className="py-2 pr-3">Item</th>
                <th className="py-2 pr-3">Purity</th>
                <th className="py-2 pr-3 text-right">Qty</th>
                <th className="py-2 pr-3 text-right">Net weight</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {receipt.items.map((item) => (
                <tr key={item.receipt_item_id} className="border-b border-border last:border-0">
                  <td className="py-2 pr-3 font-medium">
                    {item.item_name}
                    {item.serial_number ? <span className="text-muted"> · {item.serial_number}</span> : null}
                    {item.remarks ? <div className="text-sm text-muted">{item.remarks}</div> : null}
                  </td>
                  <td className="py-2 pr-3">{item.purity_label || '—'}</td>
                  <td className="tabular py-2 pr-3 text-right">{item.quantity}</td>
                  <td className="tabular py-2 pr-3 text-right">{formatWeight(item.net_weight)}</td>
                  <td className={`py-2 ${ITEM_STATUS_TONE[item.status]}`}>{ITEM_STATUS_LABEL[item.status]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card ml-auto w-full max-w-sm p-5">
        <div className="flex justify-between text-sm">
          <span className="text-muted">Total quantity</span>
          <span className="tabular font-semibold">{receipt.total_quantity}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted">Total net weight</span>
          <span className="tabular font-semibold">{formatWeight(receipt.total_net_weight)}</span>
        </div>
      </section>

      {actionError ? (
        <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          {actionError}
        </p>
      ) : null}

      <div className="raised fixed inset-x-0 bottom-0 z-20 flex items-center gap-2 border-t border-border bg-surface px-4 py-3 shadow-raised sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
        <PdfActions
          path={`/receipt/${id}/pdf`}
          documentLabel={`Receipt voucher ${receipt.voucher_no}`}
          fileBaseName={receipt.voucher_no}
          onError={setActionError}
        />
        <ThermalPrintButton printPath={`/receipt/${id}`} />
        {canCancel ? (
          <Button variant="danger" onClick={() => { setCancelError(null); setCancelOpen(true); }} className="flex-1 sm:flex-none">
            <Ban size={17} aria-hidden="true" /> Cancel
          </Button>
        ) : null}
      </div>

      <Modal open={cancelOpen} onClose={() => setCancelOpen(false)} title="Cancel this receipt?">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            The voucher number <b>{receipt.voucher_no}</b> stays reserved and is never reused. Cancelling puts every
            matched line back onto the issue voucher as still pending, and recalculates its status.
          </p>
          <TextAreaField label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} />
          {cancelError ? (
            <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
              {cancelError}
            </p>
          ) : null}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setCancelOpen(false)}>Keep receipt</Button>
            <Button
              variant="danger"
              disabled={reason.trim().length < 3}
              loading={cancelMutation.isPending}
              onClick={() => cancelMutation.mutate()}
            >
              <CheckCircle2 size={16} aria-hidden="true" /> Confirm cancellation
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
